import { useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { Button } from './Button';
import { Checkbox } from './checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './dialog';
import { useTabVisibilityStore } from '../../store/useTabVisibilityStore';
import { useDashboardLayoutStore } from '../../store/useDashboardLayoutStore';
import { useAuthStore } from '../../store/useAuthStore';
import { resolveHidden } from '../../lib/tabVisibility';
import { DASHBOARD_VIEW_ID, DASHBOARD_DEFAULT_HIDDEN } from '../dashboard/dashboardWidgets';
import { cn } from '../../lib/cn';

export interface TabOption {
  key: string;
  label: string;
  /** If true, the tab is always visible and its checkbox is disabled. */
  locked?: boolean;
}

/**
 * Dialog de réglage de visibilité des onglets d'une vue. Piloté de
 * l'extérieur (open/onOpenChange) pour pouvoir être déclenché depuis
 * le header global comme depuis un bouton inline.
 */
export function TabSettingsDialog({
  viewId,
  tabs,
  open,
  onOpenChange,
  defaultHidden,
  title = 'Personnaliser les onglets',
  description = 'Choisissez les onglets à afficher.',
}: {
  viewId: string;
  tabs: TabOption[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultHidden?: string[];
  title?: string;
  description?: string;
}) {
  // On sélectionne la DONNÉE (hidden), pas la fonction isTabHidden : sélectionner
  // une fonction (référence stable) ne déclenche jamais de re-render quand le
  // store change. On dérive isHidden localement à partir de hidden.
  const hidden = useTabVisibilityStore((s) => s.hidden);
  const toggleTab = useTabVisibilityStore((s) => s.toggleTab);
  const setHidden = useTabVisibilityStore((s) => s.setHidden);
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);
  const resetOrder = useDashboardLayoutStore((s) => s.resetOrder);
  const hiddenForView = resolveHidden(hidden[viewId], defaultHidden);
  const isHidden = (tabKey: string) => hiddenForView.includes(tabKey);
  const isDashboard = viewId === DASHBOARD_VIEW_ID;

  // Count how many non-locked tabs are currently visible
  const visibleNonLockedCount = tabs.filter(
    (t) => !t.locked && !isHidden(t.key),
  ).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm bg-white">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground -mt-2">
          {description}
        </p>

        <div className="flex flex-col gap-3 py-1">
          {tabs.map((tab) => {
            const isChecked = !isHidden(tab.key);
            // Disable unchecking if this is the last visible non-locked tab
            const wouldBeLastVisible =
              !tab.locked && isChecked && visibleNonLockedCount <= 1;
            const disabled = tab.locked || wouldBeLastVisible;

            return (
              <label
                key={tab.key}
                className={cn(
                  'flex items-center gap-3 cursor-pointer select-none',
                  disabled && 'cursor-not-allowed opacity-60',
                )}
              >
                <Checkbox
                  checked={isChecked}
                  disabled={disabled}
                  onCheckedChange={() => {
                    if (!disabled) toggleTab(viewId, tab.key, defaultHidden);
                  }}
                />
                <span className="text-sm text-neutral-800">{tab.label}</span>
                {tab.locked && (
                  <span className="text-xs text-neutral-400 ml-auto">(toujours visible)</span>
                )}
              </label>
            );
          })}
        </div>

        <DialogFooter>
          {isDashboard ? (
            <Button
              variant="ghost"
              size="sm"
              className="mr-auto"
              onClick={() => {
                setHidden(viewId, defaultHidden ?? DASHBOARD_DEFAULT_HIDDEN);
                if (collaboratorId) resetOrder(collaboratorId);
              }}
            >
              Restaurer la disposition par défaut
            </Button>
          ) : null}
          <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface TabSettingsButtonProps {
  viewId: string;
  tabs: TabOption[];
  className?: string;
}

/**
 * Bouton inline « Réglages » ouvrant le dialog de visibilité des onglets.
 * Conservé pour compatibilité ; les vues à onglets publient désormais leur
 * config vers le header via usePublishPageTabSettings.
 */
export function TabSettingsButton({ viewId, tabs, className }: TabSettingsButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="secondary"
        type="button"
        title="Personnaliser les onglets"
        className={className}
        onClick={() => setOpen(true)}
      >
        <SlidersHorizontal className="w-4 h-4 mr-1.5" /> Réglages
      </Button>

      <TabSettingsDialog viewId={viewId} tabs={tabs} open={open} onOpenChange={setOpen} />
    </>
  );
}
